<template>
  <div>
    <validation-provider
      :name="$t(`commands.data_types.${config.field_name}`)"
      :rules="config.rules ? config.rules : ''"
      v-slot="{ errors }"
    >
      <label class="d-block">
        {{ $t(config.label) }}
      </label>
      <textarea
        v-model="model"
        class="form-control comment-field__textarea"
        :class="{ 'comment-field__textarea--webhook': isWebhook }"
        :rows="isWebhook ? 7 : 4"
        :readonly="isWebhook"
        @input="$emit('input', model)"
        @click="openEditor"
      />
      <button
        v-if="isWebhook"
        type="button"
        class="btn btn-sm btn-outline-secondary mt-1"
        @click="openEditor"
      >
        {{ $t('commands.form.json_editor.expand') }}
      </button>
      <p v-if="config.hint" class="mb-0 small">
        {{ $t(config.hint) }}
      </p>
      <p v-if="errors" class="small text-danger">{{ errors[0] }}</p>
    </validation-provider>

    <div v-if="editorOpen" class="json-editor-overlay" role="dialog" aria-modal="true">
      <div class="json-editor-overlay__header">
        <div>
          <h3 class="json-editor-overlay__title">
            {{ $t('commands.form.json_editor.title') }}
          </h3>
          <small :class="jsonError ? 'text-danger' : 'text-success'">
            {{ jsonError || $t('commands.form.json_editor.valid') }}
          </small>
        </div>
        <button
          type="button"
          class="json-editor-overlay__close"
          :aria-label="$t('commands.form.json_editor.cancel')"
          @click="closeEditor"
        >
          &times;
        </button>
      </div>

      <div class="json-editor-overlay__body">
        <textarea
          ref="jsonEditor"
          v-model="editorDraft"
          class="json-editor-overlay__textarea"
          spellcheck="false"
          @keydown.tab.prevent="insertIndent"
        ></textarea>
      </div>

      <div class="json-editor-overlay__footer">
        <button type="button" class="btn btn-outline-secondary" @click="closeEditor">
          {{ $t('commands.form.json_editor.cancel') }}
        </button>
        <button
          type="button"
          class="btn btn-outline-primary"
          :disabled="Boolean(jsonError) || !editorDraft"
          @click="formatJson"
        >
          {{ $t('commands.form.json_editor.format') }}
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="Boolean(jsonError) || !editorDraft"
          @click="applyEditor"
        >
          {{ $t('commands.form.json_editor.apply') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationProvider } from "vee-validate";

export default {
  name: "CommentField",
  components: { ValidationProvider },
  props: {
    value: {
      type: String,
      default: "",
    },
    config: Object,
    context: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      model: "",
      editorDraft: "",
      editorOpen: false,
    };
  },
  created() {
    this.model = this.value != null ? String(this.value) : "";
    this.$nextTick(() => this.$emit("input", this.model));
  },
  watch: {
    value(next) {
      if (next != null && String(next) !== this.model) {
        this.model = String(next);
      }
    },
  },
  computed: {
    isWebhook() {
      return this.context?.model?.type === "webhook";
    },
    jsonError() {
      if (!this.editorDraft) return "";

      try {
        JSON.parse(this.editorDraft);
        return "";
      } catch (error) {
        return this.$t("commands.form.json_editor.invalid");
      }
    },
  },
  beforeDestroy() {
    this.restoreBodyScroll();
  },
  methods: {
    openEditor() {
      if (!this.isWebhook || this.editorOpen) return;

      this.editorDraft = this.model;
      this.editorOpen = true;
      document.body.classList.add("json-editor-open");
      window.addEventListener("keydown", this.onWindowKeydown);
      this.$nextTick(() => this.$refs.jsonEditor?.focus());
    },
    closeEditor() {
      this.editorOpen = false;
      this.restoreBodyScroll();
    },
    applyEditor() {
      if (!this.editorDraft || this.jsonError) return;

      this.model = this.editorDraft;
      this.$emit("input", this.model);
      this.closeEditor();
    },
    formatJson() {
      if (!this.editorDraft || this.jsonError) return;

      this.editorDraft = JSON.stringify(JSON.parse(this.editorDraft), null, 2);
    },
    insertIndent(event) {
      const textarea = event.target;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      this.editorDraft = `${this.editorDraft.slice(0, start)}  ${this.editorDraft.slice(end)}`;
      this.$nextTick(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      });
    },
    onWindowKeydown(event) {
      if (event.key === "Escape") this.closeEditor();
    },
    restoreBodyScroll() {
      document.body.classList.remove("json-editor-open");
      window.removeEventListener("keydown", this.onWindowKeydown);
    },
  },
};
</script>

<style lang="scss">
body.json-editor-open {
  overflow: hidden;
}
</style>

<style scoped lang="scss">
.comment-field__textarea--webhook {
  min-height: 10rem;
  cursor: pointer;
  font-family: Consolas, "Courier New", monospace;
  white-space: pre;
}

.json-editor-overlay {
  position: fixed;
  z-index: 2050;
  inset: 0;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  background: #f5f6f8;
}

.json-editor-overlay__header,
.json-editor-overlay__footer {
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  border: 1px solid #dee2e6;
  background: #fff;
}

.json-editor-overlay__header {
  justify-content: space-between;
  border-radius: 0.5rem 0.5rem 0 0;
}

.json-editor-overlay__title {
  margin: 0 0 0.25rem;
  font-size: 1.25rem;
}

.json-editor-overlay__close {
  padding: 0 0.5rem;
  border: 0;
  background: transparent;
  color: #6c757d;
  font-size: 2rem;
  line-height: 1;
}

.json-editor-overlay__body {
  display: flex;
  flex: 1;
  min-height: 0;
  border-right: 1px solid #dee2e6;
  border-left: 1px solid #dee2e6;
  background: #fff;
}

.json-editor-overlay__textarea {
  flex: 1;
  width: 100%;
  padding: 1.25rem;
  resize: none;
  border: 0;
  outline: 0;
  background: #17212b;
  color: #e8edf2;
  font-family: Consolas, "Courier New", monospace;
  font-size: 1rem;
  line-height: 1.6;
  tab-size: 2;
}

.json-editor-overlay__footer {
  justify-content: flex-end;
  gap: 0.75rem;
  border-radius: 0 0 0.5rem 0.5rem;
}

@media (max-width: 575.98px) {
  .json-editor-overlay {
    padding: 0;
  }

  .json-editor-overlay__header,
  .json-editor-overlay__footer {
    border-radius: 0;
  }
}
</style>
